import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-yaml";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/ext-language_tools";
import { ITemplate } from "./models";
import Alert from "react-bootstrap/esm/Alert";
import ButtonGroup from "react-bootstrap/esm/ButtonGroup";
import Modal from 'react-bootstrap/Modal';
import { useState } from "react";
import { apiGet } from "./api"
import { InputField } from "./components/input";
import "./TemplateEditor.scss";

interface ITemplateEditorProps {
    template: ITemplate
    onChange: ((template: ITemplate) => void);
    onCreate: ((template: ITemplate) => void);
    canChange: boolean
    create_by: boolean
    hasUem: boolean
}

enum ModalState {
    HIDE,
    COPY,
    BLANK
}

interface ITemplateModalProps {
    state: ModalState,
    template: ITemplate,
    onCancel: (() => void)
    onCreate: ((template: ITemplate) => void);
}

const safeName = (name: string): string => `${name.replace(/'+/g, "")}`; // it removes ' symbol

const createEmptyTemplate = (name: string): string =>
    `- name: ${name}

  hosts: all

  vars_prompt:

    - name: my_var
      prompt: Переменная
      help: Вы можете запрашивать у пользователя дополнительные переменные
      help_link: https://docs.ansible.com/ansible/latest/user_guide/playbooks_prompts.html
      required: yes

  tasks:

    - name: Пример задачи Ansible
      debug:
        msg: "Вы передали переменную: {{ my_var }}"`

const updateTemplateName = (name: string, yaml: string): string => {
    const txt = yaml.replace(/^- name: .*$/gm, `- name: ${name}`);
    return txt;
}

function TemplateModal({ state, template, onCancel, onCreate }: ITemplateModalProps) {

    const show = state !== ModalState.HIDE;
    const [error, setError] = useState();
    const [id, setId] = useState(template.id + "_copy");
    const [name, setName] = useState("");

    const onSubmit = (e: any) => {
        setError(undefined);
        e.preventDefault();
        const valid = e.target.checkValidity();
        e.target.reportValidity();
        if (valid) {
            apiGet("/automation/api/templates/", { check: id }).then(() => {
                const newTemplate = {
                    id,
                    name: '',
                    yaml: state === ModalState.BLANK ? createEmptyTemplate(safeName(name)) : updateTemplateName(safeName(name), template.yaml),
                    system: false,
                    changed: true
                }
                onCreate(newTemplate);
                onCancel()
            }).catch(error => {
                setError(error.params);
            });
        }
    }

    return (
        <Modal show={show} onHide={onCancel}>
            <Modal.Header closeButton>
                <Modal.Title>Создать шаблон</Modal.Title>
            </Modal.Header>
            <form onSubmit={onSubmit}>
                {error && <Alert variant="danger">{error}</Alert>}
                <Modal.Body>
                    <div className="mb-3">
                        <InputField
                            name="template_id"
                            label="ID шаблона"
                            value={id}
                            required
                            helperText="Может включать английские буквы и цифры. Должен быть уникальным."
                            onChange={(e) => setId(e.target.value)}
                        />
                    </div>
                    <div className="mb-3">
                        <InputField
                            name="template_name"
                            label="Название шаблона"
                            value={safeName(name)}
                            required
                            helperText="Название шаблона. Будет отображаться в списке шаблонов политик."
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <button type="submit" className="rd-btn rd-btn--primary rd-btn--medium">Создать</button>
                    <button type="button" className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--medium" onClick={onCancel}>Отменить</button>
                </Modal.Footer>
            </form>
        </Modal>
    )
}

function TemplateEditor({ onChange, onCreate, template, canChange, create_by, hasUem }: ITemplateEditorProps) {

    const [modal, setModal] = useState(ModalState.HIDE);

    const onChangeYAML = (yaml: string) => {
        onChange({ ...template, yaml: yaml, changed: true } as ITemplate);
    }

    return (
        <>
            <TemplateModal template={template} state={modal} onCancel={() => setModal(ModalState.HIDE)} onCreate={onCreate} />
            {(template.system && hasUem) && <div className="policy-toolbar">
                Это системный шаблон, он недоступен для редактирования. &nbsp;&nbsp;
                {
                    create_by ?
                        <ButtonGroup style={{gap: "5px"}}>
                            <button type="button" onClick={() => setModal(ModalState.COPY)} className="rd-btn rd-btn--outline rd-btn--primary rd-btn--small">Создать новый шаблон на его основе</button>
                            <button type="button" onClick={() => setModal(ModalState.BLANK)} className="rd-btn rd-btn--outline rd-btn--primary rd-btn--small">Создать новый пустой шаблон</button>
                        </ButtonGroup> : ''
                }
            </div>}
            <AceEditor
                mode="yaml"
                theme="github"
                name="policy_yaml_editor"
                width='100%'
                onChange={onChangeYAML}
                value={template.yaml}
                readOnly={template.system || !canChange}
                setOptions={{
                    useWorker: false,
                    fontSize: "1rem",
                }}
            />
        </>
    );
}

export default TemplateEditor;
